import { apiNotifications } from "@/api/notification.api.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { WorkflowRequestViewModal } from "@/pages/workflows/widgets/WorkflowRequestViewModal.tsx";
import { useEmployeeToday } from "@/queries/employees/use-employee-today.query.ts";
import { useEmployeeMe, useEmployees } from "@/queries/employees/use-employees.query.ts";
import { useNotifications } from "@/queries/use-notifications.query.ts";
import { ApiSortingDirection } from "@/types/api.types.ts";
import { EmployeeColumnId } from "@/types/column.types.ts";
import {
  ApiEmployeeFilter,
  avatarSizeToNumberMap,
  getActiveEmployeeLifecycleStatuses,
  getEmployeeAvatarUrl,
} from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Avatar,
  Box,
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TodayItem } from "../home.types.ts";

export const HomeNotifications = () => {
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const { data: employee } = useEmployeeMe();
  const { renderEmployeeName } = useFormatter();
  const { openModal } = useModals();
  const { data: todayResult } = useEmployeeToday();
  const navigate = useNavigate();

  const { data: notifications } = useNotifications({ page_number: 1 });
  const upcomingBirthdaysFilter: ApiEmployeeFilter = {
    page_number: 1,
    sorting: {
      fields: [
        {
          field: EmployeeColumnId.birth_date_relative,
          direction: ApiSortingDirection.asc,
        },
      ],
    },
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
  };
  const { data: upcomingBirthdays } = useEmployees(
    upcomingBirthdaysFilter,
    canIEdit(ApiPermission.see_employee_directory)
  );

  const [isLoadingHomeItemId, setIslLoadingHomeItemId] = useState<number | null>(null);
  const [hiddenNotificationIds, setHiddenNotificationIds] = useState<number[]>([]);

  const todayItems = useMemo(() => {
    const result: TodayItem[] = [];

    notifications?.items?.forEach((item) => {
      const currentNotification = item.notification;
      const add: TodayItem = {};

      if (!currentNotification.in_app) return;

      add.emoji = currentNotification.in_app.emoji;
      if (currentNotification.in_app.icon_url) {
        add.avatarUrl = currentNotification.in_app.icon_url.replace(
          "{size}",
          avatarSizeToNumberMap["sm"].toString()
        );
      }
      add.preTitle = currentNotification.in_app.pre_title;
      add.title = currentNotification.in_app.title;
      add.caption = currentNotification.in_app.caption;
      add.buttonTitle = currentNotification.in_app.button_title || t("common:ok");
      add.buttonIsPrimary = currentNotification.in_app.button_is_primary;
      add.showCloseButton = currentNotification.in_app.show_close_button;
      const approvalRequest = currentNotification.approval_request;
      if (approvalRequest?.workflow_data) {
        add.onView = () => {
          openModal(WorkflowRequestViewModal, {
            requestId: approvalRequest.request_id,
          });
        };
      }
      if (currentNotification.in_app.icon && !add.avatarUrl) {
        add.icon = (
          <Avatar
            icon={
              <FontAwesomeIcon
                icon={`fa-light ${currentNotification.in_app.icon}`}
                className={
                  currentNotification.in_app.icon_class
                    ? currentNotification.in_app.icon_class
                    : undefined
                }
              />
            }
          />
        );
      }

      add.onClick = async () => {
        await apiNotifications.markAsRead(currentNotification.id, true);

        setHiddenNotificationIds([...hiddenNotificationIds, currentNotification.id]);

        if (!currentNotification.in_app?.href) return;

        if (currentNotification.in_app.blank) {
          window.open(currentNotification.in_app.href, "_blank");
        } else {
          window.location.href = currentNotification.in_app.href;
        }
      };
      add.onClose = async () => {
        await apiNotifications.markAsRead(currentNotification.id, true);

        setHiddenNotificationIds([...hiddenNotificationIds, currentNotification.id]);
      };

      if (!hiddenNotificationIds.includes(currentNotification.id)) result.push(add);
    });

    if (upcomingBirthdays?.items && upcomingBirthdays.items.length > 0) {
      const today = new Date();
      const employeesWithTodayBirthdays = upcomingBirthdays.items.filter(
        (x) =>
          x.birth_date &&
          x.birth_date.month &&
          x.birth_date.month - 1 == today.getMonth() &&
          x.birth_date.day == today.getDate()
      );

      employeesWithTodayBirthdays.forEach((birthdayEmployee) => {
        result.push({
          avatarUrl: getEmployeeAvatarUrl(birthdayEmployee, "lg"),
          preTitle:
            birthdayEmployee.id === employee?.id
              ? t("calendar:happy_birthday")
              : t("core:your_teammate_birthday_today"),
          title: renderEmployeeName(birthdayEmployee),
          buttonTitle: t("core:view_profile"),
          onClick: async () => {
            openModal(EmployeeViewModal, {
              employee: birthdayEmployee,
            });
          },
        });
      });
    }

    if (todayResult && todayResult.unread_mentions_count > 0) {
      result.push({
        icon: (
          <FontAwesomeIcon
            icon={"fa-light fa-at"}
            className={
              "shrink-0 overflow-hidden flex items-center justify-center w-8 h-8 rounded-sm bg-dark/5 text-[22px]"
            }
          />
        ),
        preTitle: t("blog:you_have_mentions", {
          count_mentions: t("plurals:new_mentions", { count: todayResult.unread_mentions_count }),
        }),
        title: t("blog:check_who_mentioned"),
        buttonTitle: t("blog:view_mentions"),
        onClick: async () => {
          navigate(`/news/mentions`);
        },
      });
    }

    return result;
  }, [notifications, hiddenNotificationIds, upcomingBirthdays, todayResult]);

  return (
    <>
      {todayItems.length > 0 && (
        <>
          <Stack direction={"horizontal"} justify={"between"}>
            <Title
              header={t("common:notifications")}
              size={"lg"}
              paddingBottom
              paddingTop
              badge={`(${todayItems.length.toString()})`}
            />
          </Stack>
          <Stack gap={"sm"}>
            {todayItems.map((item, i) => (
              <Box
                className={"rounded-xs border-brand-dark/5 px-6 py-4 bg-light"}
                key={`today-${i}`}
              >
                <ListItem
                  emoji={item.emoji}
                  avatarUrl={item.avatarUrl}
                  icon={item.icon}
                  preTitle={item.preTitle}
                  title={item.title}
                  caption={item.caption}
                  emphasis={"top"}
                  behavior={"card"}
                  valueSlot={
                    <Stack direction={"horizontal"} gap={"sm"}>
                      {item.onView && (
                        <Button variant="secondary" onClick={item.onView}>
                          {t("common:view")}
                        </Button>
                      )}
                      <Button
                        isLoading={isLoadingHomeItemId == i}
                        variant={item.buttonIsPrimary ? "primary" : "secondary"}
                        onClick={async () => {
                          setIslLoadingHomeItemId(i);
                          await item.onClick?.();
                          setIslLoadingHomeItemId(null);
                        }}
                      >
                        {item.buttonTitle}
                      </Button>
                      {item.showCloseButton && (
                        <ButtonIcon
                          icon={<FontAwesomeIcon icon="fa-light fa-xmark" />}
                          variant="secondary"
                          onClick={async () => {
                            setIslLoadingHomeItemId(i);
                            await item.onClose?.();
                            setIslLoadingHomeItemId(null);
                          }}
                        />
                      )}
                    </Stack>
                  }
                />
              </Box>
            ))}
          </Stack>
        </>
      )}
    </>
  );
};
